import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useBootstrap from '../hooks/useBootstrap';
import { useCatalog } from '../hooks/useCatalog';
import PresetGroupCard from '../components/home/PresetGroupCard';
import PresetGroupCardPacks from '../components/home/PresetGroupCardPacks';
import PresetGroupCardQuad from '../components/home/PresetGroupCardQuad';
import PhotoPresetCard from '../components/catalog/PhotoPresetCard';
import VideoPresetCard from '../components/catalog/VideoPresetCard';
import { buildCatalogRubricRow } from '../core/queries/catalogRows';
import { seedPresetGroup } from '../core/queries/presetGroups';
import { localizedField } from '../locales/content';

function resolveSectionTitle(section, rubric) {
  if (section) {
    return localizedField(section, 'title', {
      fallbackSeed: section.refId || section.slug || '',
    });
  }

  if (!rubric) return '';
  return localizedField(rubric, 'name') || rubric.title || rubric.label || '';
}

function HomeRubricSkeleton({ presetCards = false }) {
  const shimmerStyle = {
    '--skeleton-base': '#151517',
  };
  const titleShimmerStyle = {
    '--skeleton-base': '#171719',
  };

  return (
    <div
      className="min-h-screen bg-bg-app pb-12 text-white"
      data-testid="home-rubric-skeleton"
    >
      <div
        className="relative shrink-0"
        style={{
          paddingTop: 'calc(var(--safe-top, 0px) + 12px)',
          paddingBottom: '12px',
        }}
      >
        <div
          className="relative mx-auto h-[21px] w-[calc(100%_-_176px)] max-w-40 rounded-md skeleton-shimmer"
          style={{
            ...titleShimmerStyle,
            transform: 'translateY(var(--page-title-shift, 47px))',
            maxWidth: 'calc(100% - 200px)',
          }}
        />
      </div>
      <div
        className="grid grid-cols-2 gap-gap-cards px-body-x"
        style={{ paddingTop: 'var(--page-title-clearance, 48px)' }}
      >
        {Array.from({ length: 6 }).map((_, index) => (
          <div
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            className="aspect-square w-full rounded-home-24 skeleton-shimmer"
            style={{
              ...shimmerStyle,
              aspectRatio: presetCards ? '3 / 4' : '1 / 1',
            }}
          />
        ))}
      </div>
    </div>
  );
}

export default function HomeRubricDetailPage() {
  const { sectionId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: bootstrap, loading, error } = useBootstrap();
  const requestedType = String(searchParams.get('type') || '').toLowerCase();
  const catalogType =
    requestedType === 'photo' || requestedType === 'video' ? requestedType : null;
  const isCatalogRubricPage = Boolean(catalogType);
  const {
    items: catalogItems,
    loading: catalogLoading,
    error: catalogError,
  } = useCatalog(catalogType || 'photo', sectionId, {
    enabled: isCatalogRubricPage && Boolean(sectionId),
  });

  useEffect(() => {
    if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
      try {
        window.scrollTo(0, 0);
      } catch {
        /* jsdom noop */
      }
    }
  }, [sectionId]);

  const section = useMemo(
    () => (bootstrap?.sections || []).find((item) => item.id === sectionId) || null,
    [bootstrap, sectionId]
  );

  const rubric = useMemo(() => {
    const photoRubrics = bootstrap?.rubrics?.photo || [];
    return photoRubrics.find((item) => item.slug === section?.refId) || null;
  }, [bootstrap, section]);

  const catalogRubric = useMemo(() => {
    if (!catalogType) return null;
    const rubrics = bootstrap?.rubrics?.[catalogType] || [];
    return rubrics.find((item) => item.slug === sectionId || item.id === sectionId) || null;
  }, [bootstrap, catalogType, sectionId]);

  const catalogRow = useMemo(
    () =>
      buildCatalogRubricRow(
        catalogType || 'photo',
        {
          slug: sectionId,
          label:
            localizedField(catalogRubric, 'name') ||
            catalogRubric?.title ||
            catalogRubric?.label ||
            sectionId ||
            '',
          cardSize: catalogRubric?.cardSize || 'M',
          order: catalogRubric?.order,
        },
        catalogItems
      ),
    [catalogItems, catalogRubric, catalogType, sectionId]
  );

  const title = isCatalogRubricPage
    ? localizedField(catalogRubric, 'name') ||
      catalogRubric?.title ||
      catalogRubric?.label ||
      catalogRow.name ||
      ''
    : resolveSectionTitle(section, rubric);
  const groups = Array.isArray(section?.items) ? section.items : [];
  const catalogPresets = Array.isArray(catalogRow?.presets) ? catalogRow.presets : [];
  const displayMode = section?.displayMode || 'single';

  const Card =
    displayMode === 'shuffle'
      ? PresetGroupCardPacks
      : displayMode === 'quad'
        ? PresetGroupCardQuad
        : PresetGroupCard;

  const handleGroupClick = useCallback(
    (group) => {
      const presets = group?.presets || [];
      if (presets.length === 1) {
        navigate(`/prompt/${presets[0].id}`);
      } else {
        seedPresetGroup(group);
        navigate(`/group/${group.id}`, { state: { group } });
      }
    },
    [navigate]
  );

  if (loading || (isCatalogRubricPage && catalogLoading)) {
    return <HomeRubricSkeleton presetCards={isCatalogRubricPage} />;
  }

  if (error || catalogError) {
    return (
      <div className="min-h-screen bg-bg-app text-white pt-30 pb-12 px-body-x">
        <div className="py-20 text-center text-text-mute">
          {t('home.loadError', 'Ошибка загрузки. Потяни вниз, чтобы обновить.')}
        </div>
      </div>
    );
  }

  const isEmptyCatalogRubric = isCatalogRubricPage && catalogPresets.length === 0;
  const isEmptyHomeSection =
    !isCatalogRubricPage &&
    (!section || section.sectionType !== 'PHOTO' || groups.length === 0);

  if (isEmptyCatalogRubric || isEmptyHomeSection) {
    return (
      <div className="min-h-screen bg-bg-app text-white pt-30 pb-12 px-body-x">
        <div className="py-20 text-center text-text-mute">
          {t('gallery.emptyCatalog', 'Пресеты отсутствуют')}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-app pb-12 text-white">
      <div
        className="relative shrink-0"
        style={{
          paddingTop: 'calc(var(--safe-top, 0px) + 12px)',
          paddingBottom: '12px',
        }}
      >
        <h1
          className="relative mx-auto w-[calc(100%_-_176px)] max-w-[260px] truncate text-center font-sans text-[18px] font-bold leading-[21px] text-text-primary"
          data-testid="home-rubric-title"
          title={title}
          style={{
            transform: 'translateY(var(--page-title-shift, 47px))',
            maxWidth: 'calc(100% - 200px)',
            marginInline: 'auto',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {title}
        </h1>
      </div>

      <div
        className="grid grid-cols-2 gap-gap-cards px-body-x"
        data-testid="home-rubric-grid"
        style={{ paddingTop: 'var(--page-title-clearance, 48px)' }}
      >
        {isCatalogRubricPage
          ? catalogPresets.map((preset) =>
              catalogType === 'video' ? (
                <VideoPresetCard
                  key={preset.id}
                  preset={preset}
                  cardSize={catalogRow.cardSize}
                />
              ) : (
                <PhotoPresetCard
                  key={preset.id}
                  preset={preset}
                  cardSize={catalogRow.cardSize}
                />
              )
            )
          : groups.map((group) => (
              <Card key={group.id} group={group} onClick={handleGroupClick} fluid />
            ))}
      </div>
    </div>
  );
}
